import { groq } from "next-sanity"

import { getEmployeeReference } from "@/graphql/schemas/shared/utils/getEmployeeReference"
import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { AppSettings } from "@/settings"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { noteProjection } from "../sanity/note.projection"

export const mutationCreateNote = {
  async createNote(parent, args, context, info) {
    const { lang = AppSettings.defaultLocale(), cdn = true, values } = args

    const { customerNumber, lineNumber } = values
    const { user } = context

    await Promise.all([
      checkPermissions({
        user,
        domainObject: DomainObjects.NOTES,
        operation: Operations.CREATE,
      }),
      validateAccessRightsToCustomer({ user, customerNumber }),
      internalCustomerException({ customerNumber }),
    ])

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const modifiedBy = await getEmployeeReference({
      employeeNumber: user.employeeNumber,
      sanityClient: client,
    })

    // create doc for inserting the data.
    // Important to only provide what is necessary
    const doc = {
      _type: "note",
      modifiedAt: new Date().toISOString(),
      customerNumber,
      text: values.text,
      displayDuringReceiptCreation: values.displayDuringReceiptCreation,
      lineNumber,
      modificationSource: "Sanity",
      ...(modifiedBy && { modifiedBy }),
    }

    // remove all undefined values
    Object.keys(doc).forEach((key) => doc[key] === undefined && delete doc[key])

    try {
      const insertNote = await client.create(doc)

      const id = insertNote._id

      const { draftsFilter } = groqQueryOptions({ includeDrafts: true })

      const note = await client.fetch(
        groq`*[_type=="note" && _id==$id ${draftsFilter}][0] {
          ${noteProjection()}
        }`,
        { id: id },
      )

      return note ?? null
    } catch (error) {
      console.warn("Error creating note", error)
      return null
    }
  },
}
